import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAppDispatch } from "../redux/hooks";
import "./Search.css";
import { fetchPreload } from "../redux/slices/preloadSlice";
import { CardContentType, CardType } from "../types";
import { cardApi } from "../api/card";
import { useUser } from "../hooks/useUser";
import { usePreload } from "../hooks/usePreload";
import SmallMockCard from "../assets/components/SmallMockCard";
import PageHeader from "./PageHeader";
import ConfirmModal from "./ConfirmModal";
import Pagination from "./Pagination";
import LanguageToggle from "./LanguageToggle";
import { normalizeSearchText } from "../utils/normalizeSearchText";
import { notify } from "../utils/notice";

type EncodingType = 'utf-8' | 'shift-jis';
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100] as const;

//type ImagesExistType = {front: boolean | null, back: boolean | null}[] | null;
const Search: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const user = useUser();
  const { userKey, userToken, /*isDarkMode*/ } = user;
  const preload = usePreload();
  const preloadLoading = preload?.loading;
  const [searchParams, setSearchParams] = useSearchParams();

  const [encoding, setEncoding] = useState<EncodingType>('utf-8');  
  const [exportImages, setExportImages] = useState(false);
  // 削除の確認モーダルで対象にしている名刺（null なら閉じている）
  const [deleteTarget, setDeleteTarget] = useState<CardType | null>(null);
  const [activeTags, setActiveTags] = useState<number[]>([]);
  const [selectedTags, setSelectedTags] = useState<Record<number, number | null>>({});

  //const [imagesExist, setImagesExist] = useState<ImagesExistType>(null);
  //const cardLoadingImage = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAEsAAAAzCAIAAACt3qe8AAAABGdBTUEAALGPC/xhBQAAAAFzUkdCAK7OHOkAAAAgY0hSTQAAeiYAAICEAAD6AAAAgOgAAHUwAADqYAAAOpgAABdwnLpRPAAAAQtJREFUeJzt1UEOgCAMBND/+ZuJRHEHMyAcKu8Q2SaZTZRXyrqqqqqqqqqqr2osQFR/E4EwC5syYq9G1Onf9mbFLNo91ON5ufp+H9H+fw8Z7/dH+N7tf0i13YjQbIxhswlBGnWaQAAAAASUVORK5CYII=';

  // light red
  //const cardErrorImage = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAFsAAAA3CAIAAADsYqHvAAABhWlDQ1BJQ0MgcHJvZmlsZQAAKJF9kT1Iw0AcxV9bpVoqHewg4pChOogFURFHrUIRKoRaoVUHk0u/oElDkuLiKLgWHPxYrDq4OOvq4CoIgh8gzg5Oii5S4v+SQosYD4778e7e4+4d4G9UmGp2jQOqZhnpZELI5laF4CvCCKEXo4hIzNTnRDEFz/F1Dx9f7+I8y/vcn6NPyZsM8AnEs0w3LOIN4ulNS+e8TxxlJUkhPiceM+iCxI9cl11+41x02M8zo0YmPU8cJRaKHSx3MCsZKvEUcUxRNcr3Z11WOG9xVis11ronf2E4r60sc53mEJJYxBJECJBRQxkVWIjTqpFiIk37CQ//oOMXySWTqwxGjgVUoUJy/OB/8LtbszA54SaFE0D3i21/DAPBXaBZt+3vY9tungCBZ+BKa/urDWDmk/R6W4sdAZFt4OK6rcl7wOUOMPCkS4bkSAGa/kIBeD+jb8oB/bdAaM3trbWP0wcgQ12lboCDQ2CkSNnrHu/u6ezt3zOt/n4AUEdymdyj8JUAAAAJcEhZcwAALiMAAC4jAXilP3YAAAAHdElNRQfoDAoJKxIWlomEAAAAGXRFWHRDb21tZW50AENyZWF0ZWQgd2l0aCBHSU1QV4EOFwAAAFxJREFUaN7t0DEBAAAEADD0rygLrwK+LcJyuoOjFBgxYsSIESNGjBgxYsSIESNGjGDEiBEjRowYMWLEiBEjRowYMYIRI0aMGDFixIgRI0aMGDFixAhGjBgxYuTNAivRAycGgLmXAAAAAElFTkSuQmCC';

  // white with red stripe
  const cardErrorImage = ' data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAFsAAAA3CAYAAABjADa4AAABhWlDQ1BJQ0MgcHJvZmlsZQAAKJF9kT1Iw0AcxV9bpVoqHewg4pChOogFURFHrUIRKoRaoVUHk0u/oElDkuLiKLgWHPxYrDq4OOvq4CoIgh8gzg5Oii5S4v+SQosYD4778e7e4+4d4G9UmGp2jQOqZhnpZELI5laF4CvCCKEXo4hIzNTnRDEFz/F1Dx9f7+I8y/vcn6NPyZsM8AnEs0w3LOIN4ulNS+e8TxxlJUkhPiceM+iCxI9cl11+41x02M8zo0YmPU8cJRaKHSx3MCsZKvEUcUxRNcr3Z11WOG9xVis11ronf2E4r60sc53mEJJYxBJECJBRQxkVWIjTqpFiIk37CQ//oOMXySWTqwxGjgVUoUJy/OB/8LtbszA54SaFE0D3i21/DAPBXaBZt+3vY9tungCBZ+BKa/urDWDmk/R6W4sdAZFt4OK6rcl7wOUOMPCkS4bkSAGa/kIBeD+jb8oB/bdAaM3trbWP0wcgQ12lboCDQ2CkSNnrHu/u6ezt3zOt/n4AUEdymdyj8JUAAAAJcEhZcwAALiMAAC4jAXilP3YAAAAHdElNRQfoDAoJOB5+z4Q9AAAAGXRFWHRDb21tZW50AENyZWF0ZWQgd2l0aCBHSU1QV4EOFwAAAh9JREFUeNrt281ugzAQBOBxgMAh7/+YOYWIHwM9bC1oBampwWA8K+VStZev0WxmW9TwfA5IEiDPgfsdUAqcfeYGAOg64P0GXi+gbamyK7YZrQW8LOUXwNl0UigFDMPPrzaNvMPzXF63G6U2GDVoPaCuBfg3OiDQBp157og9fAtrDVTVcmYnCVAUskQ5jtjTCKmq5czOMkFPU+o5YwMSJ3Utr76f+Skl7/A8l3c8xwHbTN+P6HPfphSX6GbYZrpOoqVpFj5A3sY85xJ1xDbTtoKu9cIHyVTQs4yyztgmz5tGomVpiZo85xJ1xF67RIuCee6MPc1zliJP2NO7ik0pyrJo0bfDnpaiul5eohGXou2xp0u0qpbz3KBHVIr2wWYpOgCbpegAbJaiA7BNnhv0yC6L/rEjLkXHYU+XqMnzi5ei47EjKkXnwY5giZ4P26YUAWOeB1SKzol90VJ0buyLlaIwsG2X6MnzPCzswEtReNhrS9GJ/t0iXOwAS1H42NM8N3+eO2kpug52AKXoeti2pShJgMfD6+fza2LbliLP0XJt7N+lqG3n0T1FSxzY0zwvy3lwD/eWuLAPvrfEh20bLTvcW+LFtr23bPh4C7E93luI7fHeQuz/3FuUGqNlxRIlts0S/XRvWfHMP7E93luIvfbe8tfjLR9KEbE9liJiu+T5ysdbiO2xFBF7q7F45p/YHksRsT0uUWJ7LEXE9liKvgB/QkyFKQ7R8AAAAABJRU5ErkJggg==';

  // 状態変数（初期値は空、URLパラメータからの読み込みはuseEffectで行う）
  const [searchWords, setSearchWords] = useState("");
  const [sort, setSort] = useState<keyof CardContentType | "cardKey" | null>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [language, setLanguage] = useState<"japanese" | "english">("japanese");
  const [cardsPerPage, setCardsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  // can't be "loading" because that's the default state, so we never set it to that, we just assume that's what it is if there's no set value
  const [imageStatus, setImageStatus] = useState<{ front: 'success' | 'error' | null, back: 'success' | 'error' | null }[]>([]);
  const [imageHover, setImageHover] = useState<boolean[]>([]);

  // URLパラメータを更新し履歴に追加する共通関数
  const updateUrlParams = (updates: Record<string, string | null>) => {
    const newParams = new URLSearchParams(searchParams);
    
    Object.entries(updates).forEach(([key, value]) => {
      if (value === null || value === "" || value === "{}") {
        newParams.delete(key);
      } else {
        newParams.set(key, value);
      }
    });
    
    setSearchParams(newParams, { replace: false });
  };

  // Fetch the preload data if it's not already loaded
  useEffect(() => {
    if (!preload && preloadLoading !== true && userKey && userToken) {
      dispatch(fetchPreload());
    }
  }, [preload, preloadLoading, dispatch, userKey, userToken]);

  // URLパラメータが変更された時に状態を同期（ブラウザの戻るボタン対応）
  // 依存配列はsearchParamsのみ（ローカル状態の変更では発火させない）
  useEffect(() => {
    const tagsParam = searchParams.get('tags');
    const searchParam = searchParams.get('search');
    const langParam = searchParams.get('lang');
    
    // タグフィルタの同期（値が変わった場合のみ更新）
    const newTags = tagsParam ? JSON.parse(tagsParam) : {};
    if (JSON.stringify(selectedTags) !== JSON.stringify(newTags)) {
      setSelectedTags(newTags);
    }
    
    // 検索ワードの同期（値が変わった場合のみ更新）
    const newSearch = searchParam || "";
    if (searchWords !== newSearch) {
      setSearchWords(newSearch);
    }
    
    // 言語の同期（値が変わった場合のみ更新）
    const newLang = (langParam === "japanese" || langParam === "english") ? langParam : "japanese";
    if (language !== newLang) {
      setLanguage(newLang);
    }
    
    // ソートは履歴管理しないため、URLパラメータから復元しない
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // 検索ワードのデバウンス処理（入力が止まってから500ms後にURLパラメータを更新）
  useEffect(() => {
    const timer = setTimeout(() => {
      const currentSearchParam = searchParams.get('search') || "";
      // URLパラメータと現在の検索ワードが異なる場合のみ更新
      if (currentSearchParam !== searchWords) {
        // キーワードが空の場合はURLパラメータの更新自体を行わない（履歴に追加しない）
        if (searchWords !== "") {
          updateUrlParams({ search: searchWords });
        }
      }
    }, 500);

    // クリーンアップ：次の入力があった場合はタイマーをキャンセル
    return () => clearTimeout(timer);
  }, [searchWords]);

  const allTags = preload?.tags || [];

  const tagGroups = allTags.reduce<Record<number, { title: string, english: string | undefined, japanese: string | undefined, options: { tagKey: number; name: string, english: string | undefined, japanese: string | undefined }[] }>>((groups, tag) => {
    if (tag.parentKey === null) {
      groups[tag.tagKey] = { title: tag.name, english: tag.english, japanese: tag.japanese, options: [] };
    } else if (tag.parentKey in groups) {
      groups[tag.parentKey].options.push({ tagKey: tag.tagKey, name: tag.name, english: tag.english, japanese: tag.japanese });
    }
    return groups;
  }, {});
  //console.log("tagGroups");
  //console.log(tagGroups);
  //console.log("selectedTags");
  //console.log(selectedTags);

  function handleTagChange(parentKey: number, selectedTagKey: number | null) {
    const newSelectedTags: Record<number, number | null> = { ...selectedTags };
    
    if (selectedTagKey === null) {
      delete newSelectedTags[parentKey];
    } else {
      newSelectedTags[parentKey] = selectedTagKey;
    }
    
    setSelectedTags(newSelectedTags);
    
    // URLパラメータを更新（履歴にpush）
    updateUrlParams({
      tags: Object.keys(newSelectedTags).length > 0 ? JSON.stringify(newSelectedTags) : null
    });
  }

  const getFilteredTagKeys = (tagGroups: Record<number, any>, selectedTags: Record<number, number | null>): number[] => {
    let result: number[] = [];

    for (const [groupKey, selectedTagKey] of Object.entries(selectedTags)) {
      const group = tagGroups[Number(groupKey)];
      if (!group || !group.options) continue;

      // Extract all tagKeys from the options array
      const allTagKeys = group.options.map((option: { tagKey: number }) => option.tagKey);

      // Exclude the selected tag key
      const filteredKeys = allTagKeys.filter((tagKey: number) => tagKey !== selectedTagKey);

      // Add to result
      result.push(...filteredKeys);
    }

    return result;
  };

  const getRequiredTagKeys = (tagGroups: Record<number, any>, selectedTags: Record<number, number | null>): number[] => {
    let result: number[] = [];

    for (const [groupKey, selectedTagKey] of Object.entries(selectedTags)) {
      if(selectedTagKey) {
        result.push(selectedTagKey as number);
      }
    }
    return result;
  };

  const tagDropdownElements = (
    <div className="c-fieldGroup">
      {Object.entries(tagGroups).map(([parentKey, group]) => (
        <div key={parentKey} className="c-fieldGroup__item">
          <label className="c-label" htmlFor={`tag-${parentKey}`}>
            {(language === "japanese" ? group.japanese : group.english) || group.title}
          </label>
          <select
            id={`tag-${parentKey}`}
            className="c-input"
            value={selectedTags[+parentKey] || ""}
            onChange={(e) =>
              handleTagChange(+parentKey, e.target.value ? +e.target.value : null)
            }
          >
            <option value="">
              {language === "japanese" ? "タグを選択してください" : "Choose"}
            </option>
            {group.options.map((option) => (
              <option key={option.tagKey} value={option.tagKey}>
                {(language === "japanese" ? option.japanese : option.english) ||
                  option.name}
              </option>
            ))}
          </select>
        </div>
      ))}
    </div>
  );

  //    useEffect(() => {
  //      if (!preload) return;
  //
  //      const fetchImagesExist = async () => {
  //        const newImagesExist: ImagesExistType = [];
  //
  //        await Promise.all(
  //          preload.cards.map(async (card) => {
  //            const frontUrl = `https://cards.livelet.dev/images/thumbnails/card${card.cardKey}front.jpg`;
  //            const backUrl = `https://cards.livelet.dev/images/thumbnails/card${card.cardKey}back.jpg`;
  //            const newImageExistsObject = {
  //              front: await testFileExists(frontUrl),
  //              back: await testFileExists(backUrl),
  //            };
  //            newImagesExist[card.cardKey] = newImageExistsObject;
  //          })
  //        );
  //
  //        setImagesExist(newImagesExist);
  //      };
  //
  //      fetchImagesExist();
  //    }, [preload]);


  // フィルタリング処理をメモ化して無限ループを防ぐ
  const fullyFilteredCards = useMemo(() => {
    const allContentfulCards = preload?.cards?.filter(card => card.content.firstName && card.content.lastName) || [];

    const requiredTags = getRequiredTagKeys(tagGroups, selectedTags);
    
    const tagsFilteredCards = allContentfulCards?.filter(card => {
      const cardTags = card.tags;
      if (requiredTags.length === 0) return true;
      const passesFilter = requiredTags.every(tag => cardTags.includes(tag));
      return passesFilter;
    });

    // 半角・全角スペースで区切り、すべての語がどれかの項目に部分一致する名刺を出す（「田中 広志」「ライブレット 甲府」など。2026-11-02）
    // 全角スペースは normalizeSearchText（NFKC）で半角になる
    const searchTerms = normalizeSearchText(searchWords).split(/\s+/).filter(term => term !== "");
    const keywordFilteredCards = searchTerms.length === 0 ? tagsFilteredCards : tagsFilteredCards?.filter(card => {
      // 名刺の全項目の値（日本語・英語とも）をそろえてから比べる
      const cardContent = card.content;
      const fieldTexts = (Object.keys(cardContent) as Array<keyof typeof cardContent>).flatMap(field => {
        const fieldData = cardContent[field];
        return Array.isArray(fieldData) ? fieldData.filter(obj => obj.content).map(obj => normalizeSearchText(obj.content)) : [];
      });
      return searchTerms.every(term => fieldTexts.some(text => text.includes(term)));
    });

    const langFilteredCards = keywordFilteredCards?.map((card) => {
      const cardContent = card.content;

      const filteredContent = (Object.keys(cardContent) as Array<keyof typeof cardContent>).reduce((acc, key) => {
        const fieldData = cardContent[key];
        if (!fieldData) {
          acc[key] = null;
          return acc;
        }
        const matched = fieldData.filter((obj) => obj.language === language);
        acc[key] = matched.length > 0 ? matched : fieldData;
        return acc;
      }, {} as typeof cardContent);

      return {
        ...card,
        content: filteredContent,
      };
    });

    return langFilteredCards;
  }, [preload?.cards, selectedTags, searchWords, language]);

  const sortedCards = useMemo(() => {
    if (!sort) return fullyFilteredCards;

    return [...fullyFilteredCards].sort((a, b) => {
      const aContent = sort === "cardKey" ? a.cardKey.toString() : a.content[sort]?.[0].content || "";
      const bContent = sort === "cardKey" ? b.cardKey.toString() : b.content[sort]?.[0].content || "";
      return sortOrder === "asc" ?
        (sort === "cardKey" ? a.cardKey > b.cardKey ? 1 : -1 : aContent.localeCompare(bContent)) :
        (sort === "cardKey" ? a.cardKey < b.cardKey ? 1 : -1 : bContent.localeCompare(aContent));
    });
  }, [sort, sortOrder, fullyFilteredCards]);

  function HandleSortChange(value: keyof CardContentType | "cardKey" | null) {
    if (!value || !fullyFilteredCards) return;

    const newSortOrder = sort === value ? (sortOrder === "asc" ? "desc" : "asc") : "asc";
    setSort(value);
    setSortOrder(newSortOrder);
    // ソートは履歴管理しないため、URLパラメータは更新しない
  }

  const cardsToDisplay = sortedCards;
  const totalPages = Math.max(1, Math.ceil(cardsToDisplay.length / cardsPerPage));
  const pageStart = (currentPage - 1) * cardsPerPage;
  const paginatedCards = cardsToDisplay.slice(pageStart, pageStart + cardsPerPage);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  function changePage(page: number) {
    setCurrentPage(page);
  }

  function handleImageLoad(cardKey: number, face: 'front' | 'back') {
    if (imageStatus[cardKey]?.[face] === "success") return;
    if (imageStatus[cardKey]?.[face] === "error") return;
    setImageStatus((prev) => {
      const newStatus = [...prev];
      newStatus[cardKey] = { ...newStatus[cardKey], [face]: "success" };
      return newStatus;
    });
  }

  function handleImageError(cardKey: number, face: 'front' | 'back') {
    setImageStatus((prev) => {
      const newStatus = [...prev];
      newStatus[cardKey] = { ...newStatus[cardKey], [face]: "error" };
      return newStatus;
    });
  }

  const handleExportCards = async () => {
    try {
      const userKey = 2;
      const token = '7e16b5527c77ea58';
      const response = await cardApi.exportCards(userKey, token, exportImages, encoding);

      // Create a download link for the received file
      const url = window.URL.createObjectURL(response);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'cards_export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Failed to export cards:', error);
      notify('名刺情報のエクスポート時にエラーが発生しました。', 'エラー');
    }
  };

  const tags = preload?.tags || [];
  const tagElements = tags.map((tag) => (
    <div
      key={tag.tagKey}
      className="btn"
      style={{
        backgroundColor: activeTags.includes(tag.tagKey) ? "#35387b" : "#e0e0e0",
        color: activeTags.includes(tag.tagKey) ? "#fff" : "#666",
        width: "8rem",
        fontSize: "1rem",
      }}
      onClick={() => {
        setActiveTags(prev => {
          // Check if tagKey is already in the array
          if (prev.includes(tag.tagKey)) {
            // If it is, remove it by filtering it out
            return prev.filter(key => key !== tag.tagKey);
          } else {
            // If it's not, add it to the array
            return [...prev, tag.tagKey];
          }
        });
      }}
    >
      {language === "english" ? tag.english : language === "japanese" ? tag.japanese : tag.name}
    </div>
  ));
  const allTag = (
    <div
      key={0}
      className="btn"
      style={{
        backgroundColor: activeTags.length === 0 ? "#35387b" : "#e0e0e0",
        color: activeTags.length === 0 ? "#fff" : "#666",
        width: "8rem",
        fontSize: "1rem",
      }}
      onClick={() => { setActiveTags([]); }}
    >
      {language === "japanese" ? "すべて" : "All"}
    </div>
  );
  tagElements.unshift(allTag);

  const tagsElement = (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", padding: "0.5rem 0" }}>
      {tagElements}
    </div>
  );

  const handleDeleteConfirmed = async () => {
    const target = deleteTarget;
    setDeleteTarget(null);
    if (!target || !userKey || !userToken) return;
    try {
      await cardApi.deleteCard(target.cardKey, userKey, userToken);
      // 削除できたら一覧を取り直す
      dispatch(fetchPreload());
    } catch (error) {
      console.error("Failed to delete card or fetch updated data:", error);
    }
  };

  const deleteTargetName = deleteTarget
    ? [deleteTarget.content.lastName?.[0]?.content, deleteTarget.content.firstName?.[0]?.content].filter(Boolean).join(" ")
    : "";
  const deleteTargetCompany = deleteTarget?.content.companyName?.[0]?.content ?? "";

  const list = paginatedCards.map((card) => {
    // 画像が無い面は読み込まずに「エラー」扱いにする（存在しない画像を取りに行って 404 を出さない。2026-10-13）
    const loadedImages = {
      ...imageStatus[card.cardKey],
      ...(card.hasFrontImage ? {} : { front: "error" as const }),
      ...(card.hasBackImage ? {} : { back: "error" as const }),
    };
    const loadedCount = loadedImages ? Object.values(loadedImages).filter((status) => status === "success").length : 0;
    const errorCount = loadedImages ? Object.values(loadedImages).filter((status) => status === "error").length : 0;
    const loadingCount = 2 - loadedCount - errorCount;

    const baseURL = import.meta.env.VITE_API_URL;

    //const baseUrl = typeof window !== "undefined" ? window.location.origin : "";
    const isFrontImageError = loadedImages?.front === "error";
    const frontImageSrc = isFrontImageError ? cardErrorImage : `${baseURL}/images/thumbnails/card${card.cardKey}front.jpg`;

    const isBackImageError = loadedImages?.back === "error";
    const backImageSrc = isBackImageError ? cardErrorImage : `${baseURL}/images/thumbnails/card${card.cardKey}back.jpg`;

    // 検索画面から来たことを示すパラメータを追加（戻るボタンで検索画面に戻れるように）
    const cardDetailUrl = `/camera/${card.cardKey}?from=search`;

    return (
      <div key={card.cardKey} className="search-results__item">
        <div className="grid items-center relative search-results-row border-b border-primary-bordergray min-w-[850px]" style={{ minHeight: "4rem" }}>
          <span className="cardElement text-xs">{card.cardKey}</span>
          <Link to={cardDetailUrl} className="link-inherit">
            <div className={`imagesDisplay imagesDisplay${loadedCount} imagesDisplay${loadingCount} flex items-center justify-start`} onMouseEnter={() => {
              setImageHover((prev) => {
                const newHover = [...prev];
                newHover[card.cardKey] = true;
                return newHover;
              });
            }} onMouseLeave={() => {
              setImageHover((prev) => {
                const newHover = [...prev];
                newHover[card.cardKey] = false;
                return newHover;
              });
            }
            }>
              <img src={frontImageSrc} className={`drop-shadow-md cardImage ${imageHover[card.cardKey] === true ? "frontHover" : ""} front${loadedImages?.front || "loading"}`} onLoad={() => { handleImageLoad(card.cardKey, "front") }} onError={() => { handleImageError(card.cardKey, "front") }} />
              <img src={backImageSrc} className={`cardImage drop-shadow-md ${imageHover[card.cardKey] === true ? "backHover" : ""} back${loadedImages?.back || "loading"}`} onLoad={() => { handleImageLoad(card.cardKey, "back") }} onError={() => { handleImageError(card.cardKey, "back") }} />
              {/*<img src={cardErrorImage} className="cardImage placeholderError" style={{ display: errorCount === 2 ? undefined : "none" }} />*/}
              {errorCount === 2 ? <div className={`cardImage drop-shadow-md ${imageHover[card.cardKey] === true ? "frontHover" : ""}`}><SmallMockCard content={card.content} language={language} width={50}/></div> : null}
            </div>
          </Link>
          <Link to={cardDetailUrl} className="link-inherit"><span className="text-sm cardElement">{card.content.lastName?.[0].content} {card.content.firstName?.[0].content}</span></Link>
          <Link to={cardDetailUrl} className="link-inherit"><span className="text-sm cardElement">{card.content.companyName?.[0].content}</span></Link>
          <Link to={cardDetailUrl} className="link-inherit"><span className="text-sm cardElement">{card.content.title?.[0].content}</span></Link>
          <Link to={cardDetailUrl} className="link-inherit"><span className="text-sm cardElement">{card.content.email?.[0].content}</span></Link>
          <Link to={cardDetailUrl} className="link-inherit"><span className="text-sm cardElement">{card.content.address?.[0].content}</span></Link>
          <span className="cardElement search-results__delete">
            <button
              type="button"
              className="delete-icon"
              aria-label={`名刺 #${card.cardKey} を削除`}
              onClick={() => setDeleteTarget(card)}
            />
          </span>
        </div>
      </div>

    )

  })

  const languageSelector = (
    <LanguageToggle
      value={language}
      onChange={(lang) => {
        setLanguage(lang);
        // URLパラメータを更新（履歴にpush）
        updateUrlParams({ lang });
      }}
    />
  );

  const searchbar = (
    <input
      type="text"
      placeholder="キーワードを入力"
      aria-label="キーワード"
      value={searchWords || ""}
      onChange={(event) => {
        const newValue = event.target.value;
        setSearchWords(newValue);
        // URLパラメータの更新はデバウンスuseEffectで行う
      }}
      className="c-input"
    />
  )

  const resultsHeader = (
    <div className="grid search-results-row search-results-head text-sm">
      <div onClick={() => HandleSortChange("cardKey")}
        className={sort == "cardKey" ? "cardElement-on" : "cardElement"}>#</div>
      <div className="cardElement">画像</div>
      <div onClick={() => HandleSortChange("lastName")}
        className={sort == "lastName" ? "cardElement-on" : "cardElement"}>氏名</div>
      <div onClick={() => HandleSortChange("companyName")}
        className={sort == "companyName" ? "cardElement-on" : "cardElement"}>会社名</div>
      <div onClick={() => HandleSortChange("title")}
        className={sort == "title" ? "cardElement-on" : "cardElement"}>役職</div>
      <div onClick={() => HandleSortChange("email")}
        className={sort == "email" ? "cardElement-on" : "cardElement"}>メール</div>
      <div onClick={() => HandleSortChange("address")}
        className={sort == "address" ? "cardElement-on" : "cardElement"}>会社住所</div>
      <div className="cardElement search-results__delete">削除</div>
    </div>
  )

  const pagination = (
    <Pagination
      currentPage={currentPage}
      totalPages={totalPages}
      pageSize={cardsPerPage}
      pageSizeOptions={PAGE_SIZE_OPTIONS}
      onPageChange={changePage}
      onPageSizeChange={(size) => { setCardsPerPage(size); setCurrentPage(1); }}
    />
  )

  const results = (
    <div className="w-full overflow-x-auto relative rounded-md bg-white">
      {resultsHeader}
      <div className="bg-white">
        {list}
      </div>
    </div>
  )

  const exportElements = (
    <div className="searchExport">
      <button
        className="btn btn-primary searchExport__button"
        onClick={handleExportCards}
      >
        CSV出力
      </button>
      <select
        value={encoding}
        onChange={(e) => setEncoding(e.target.value as EncodingType)}
        className="c-input searchExport__encoding"
        aria-label="文字コード"
      >
        <option value="utf-8">UTF-8</option>
        <option value="shift-jis">Shift-JIS</option>
      </select>
      <div className="hidden">
        <input
          type="checkbox"
          checked={exportImages}
          onChange={() => setExportImages(!exportImages)}
        />
        <span className="ml-2">画像もCSV出力する</span>
      </div>
    </div>
  );

  return (
    <div className="top-page-container">
      <PageHeader title="名刺の検索" showBack />
      <div className="pageBody">
        <div className="searchForm">
          {searchbar}
          {tagDropdownElements}
        </div>
        {/* 言語設定とページ送りを1行に（PC）。スマホは言語設定の下にページ送り */}
        <div className="searchToolbar">
          {languageSelector}
          <div>
            <span className="c-label" id="paging-label">表示設定</span>
            <div role="group" aria-labelledby="paging-label">
              {pagination}
            </div>
          </div>
        </div>
        {results}
        {exportElements}
      </div>
      <ConfirmModal
        open={deleteTarget !== null}
        title="名刺を削除しますか？"
        message={<>
          <p>{deleteTargetName || `名刺 #${deleteTarget?.cardKey ?? ""}`}{deleteTargetCompany && `（${deleteTargetCompany}）`}の名刺を削除します。</p>
          <p>この操作は取り消せません。</p>
        </>}
        confirmLabel="削除する"
        danger
        onConfirm={handleDeleteConfirmed}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  )
  return (
    <div className="container-search p-2 md:p-8">
      <div className="flex justify-start">
        <span onClick={() => navigate(-1)} className="text-3xl md:text-2xl font-bold pr-1">←</span>
        <h1 className="text-3xl md:text-2xl font-bold">検索</h1>
      </div>
      {preload ? (
        <div>
          <div className="md:flex md:justify-between mt-7">
            <div className='p-2'>
              <div className="btn btn-primary md:w-28" onClick={handleExportCards}>
                CSV出力
              </div>
              <div className='mt-2'>
                <input type="checkbox" checked={exportImages} onChange={() => setExportImages(!exportImages)} />
                <span className="ml-2">画像もCSV出力する</span>
              </div>
            </div>
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", padding: "0.5rem 0" }}>
            {tagElements}
          </div>

          {languageSelector}

          <div className="drop-shadow-md overflow-x-auto relative">
            <div className="bg-primary-gray grid gap-4 px-3" style={{ gridTemplateColumns: '40px 50px 100px repeat(5, minmax(150px, 1fr))' }}>
              <div className="cardElement">#</div>
              <div className="cardElement">画像</div>
              <div onClick={() => HandleSortChange("lastName")}
                className={sort == "lastName" ? "cardElement-on" : "cardElement"}>氏名</div>
              <div onClick={() => HandleSortChange("companyName")}
                className={sort == "companyName" ? "cardElement-on" : "cardElement"}>会社名</div>
              <div onClick={() => HandleSortChange("title")}
                className={sort == "title" ? "cardElement-on" : "cardElement"}>役職</div>
              <div onClick={() => HandleSortChange("email")}
                className={sort == "email" ? "cardElement-on" : "cardElement"}>メール</div>
              <div onClick={() => HandleSortChange("address")}
                className={sort == "address" ? "cardElement-on" : "cardElement"}>会社住所</div>
              <div className="cardElement">削除</div>
            </div>
            <div className="bg-white">
              {list}
            </div>

          </div>

        </div>
      ) : (
        !preloadLoading && <span>No data available</span>
      )}
    </div>
  );
};

export default Search;
